'use client';
import 'reflect-metadata';
import '@web-app/styles/globals.css';
import 'stream-chat-react/dist/css/v2/index.css';
import '@web-app/styles/stream-chat-theme.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '@web-app/styles/provider-calendar.css';
import '@web-app/styles/picker-week.css';

import { ChakraProvider } from '@chakra-ui/react';
import { ClerkProvider } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { CacheProvider } from '@emotion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { appStore } from '@web-app/atoms/appStore';
import { SetDatadogUser } from '@web-app/components/DatadogSetup/SetDatadogUser';
import ErrorBoundary from '@web-app/components/ErrorBoundary';
import { PersistentLayout } from '@web-app/components/PersistentLayout';
import { PrefetchProviderConfig } from '@web-app/components/PrefetchProviderConfig';
import { AnalyticsProvider } from '@web-app/context/AnalyticsContext';
import { AppContext } from '@web-app/hocs/app-context';
import GrowthBookProvider from '@web-app/hocs/growthbook';
import { OrganizationProvider } from '@web-app/hocs/organization';
import { chakraLayerCache } from '@web-app/theming/chakra-layer-cache';
import {
  getDomainFromWindow,
  whiteLabelDomains,
} from '@web-app/utils/api/whitelabel/organization-configs';
import { trpc } from '@web-app/utils/trpc';
import { Provider as JotaiProvider } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Toaster } from 'sonner';

import theme from '../theming/theme';

/**
 * App-wide StreamChat connection — manages client lifecycle and writes
 * chatClientAtom + chatUnreadCountAtom for the Navbar badge and /messages page.
 * Requires browser APIs (WebSocket), so ssr: false.
 */
const ChatConnection = dynamic(
  () =>
    import('@web-app/components/Chat/ChatConnection').then(
      (mod) => mod.ChatConnection,
    ),
  { ssr: false },
);

/**
 * Global chat drawer — slides in from the right to show a single conversation.
 * Opened via useChatDrawer().openChat(patientHealthieId) from any page.
 * Depends on chatClientAtom (populated by ChatConnection), so ssr: false.
 */
const ChatDrawer = dynamic(
  () =>
    import('@web-app/components/Chat/ChatDrawer').then((mod) => mod.ChatDrawer),
  { ssr: false },
);

/**
 * Global scheduling drawer — slides in from left (in-session) or right (profile).
 * Opened via useSchedulingDrawer().openSchedule(patientId, name) from any page.
 */
const SchedulingDrawer = dynamic(
  () =>
    import('@web-app/components/Scheduling/SchedulingDrawer').then(
      (mod) => mod.SchedulingDrawer,
    ),
  { ssr: false },
);

/**
 * Hidden Photon auth bootstrap for SSO callbacks. The prescription decision
 * dialog portals to `document.body`, so prescribe elements cannot rely on a
 * React-ancestor `<photon-client>` — they need their own client in the panel.
 * This root exists only to process `/prescription-review?photon=true` on page
 * load before the dialog mounts. Mount as a sibling in `_app` (not a page
 * wrapper). ssr: false.
 */
const PhotonClientRoot = dynamic(
  () =>
    import('@web-app/components/Photon/PhotonClientRoot').then(
      (mod) => mod.PhotonClientRoot,
    ),
  { ssr: false },
);

/**
 * Syncs the React Query QueryClient into Jotai's store so that
 * `atomWithQuery` (from jotai-tanstack-query) uses the same client
 * as `useQueryClient()`. Without this, invalidateQueries() calls
 * from hooks won't trigger refetches on atom-based queries.
 */
function HydrateAtoms({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: React.ReactNode;
}) {
  useHydrateAtoms([[queryClientAtom, queryClient] as const]);
  return children;
}

const SERVICE_NAME = 'provider-portal';
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || 'development';

function App({ Component, pageProps }: AppProps) {
  const searchParams = useSearchParams();
  const [betaFeaturesEnabled, setBetaFeaturesEnabled] = useState(false);
  const [isSatellite, setIsSatellite] = useState<boolean>();
  const [signInUrl, setSignInUrl] = useState<string>();
  const [signUpUrl, setSignUpUrl] = useState<string>();
  const [domain, setDomain] = useState<string>('');

  useEffect(() => {
    datadogRum.init({
      applicationId: '4ac78d2e-2e0f-4967-9cd1-96c88f09c0ca',
      clientToken: 'pub8d308f5071cfd21fba11eab5b80e4fdb',
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: 'datadoghq.com',
      service: SERVICE_NAME,
      env: process.env.NEXT_PUBLIC_APP_ENV || 'development',
      version: APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      compressIntakeRequests: true,
    });

    datadogLogs.init({
      clientToken: 'pub921d3dbc89af423e52d19b0ba44d0b9e',
      site: 'datadoghq.com',
      service: SERVICE_NAME,
      env: process.env.NEXT_PUBLIC_APP_ENV || 'development',
      version: APP_VERSION,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  }, []);

  useEffect(() => {
    const allFlagsAreEnabled = searchParams?.get('beta') === 'true';

    if (allFlagsAreEnabled) {
      setBetaFeaturesEnabled(true);
    }
  }, [searchParams]);

  // Use useState with lazy initializer to create QueryClient ONCE
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 3,
            retryDelay: 10 * 1000,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY?.startsWith('pk_test')) {
      setIsSatellite(false);
    } else {
      const isSatellite = whiteLabelDomains.some((it) =>
        window.location.hostname.includes(it),
      );
      const berryStreetOrigin = 'https://meet.berrystreet.co';
      setDomain(getDomainFromWindow());
      setSignInUrl(!isSatellite ? '/sign-in' : `${berryStreetOrigin}/sign-in`);
      setSignUpUrl(!isSatellite ? '/sign-up' : `${berryStreetOrigin}/sign-up`);
      setIsSatellite(isSatellite);
    }
  }, []);

  return (
    <ErrorBoundary>
      <AppContext.Provider
        value={{
          betaFeaturesEnabled,
          setBetaFeaturesEnabled,
        }}
      >
        {isSatellite === undefined ? null : (
          <ClerkProvider
            isSatellite={isSatellite}
            signInUrl={signInUrl}
            signUpUrl={signUpUrl}
            domain={domain}
            allowedRedirectOrigins={
              isSatellite ? [window.location.origin] : undefined
            }
          >
            <SetDatadogUser />
            <QueryClientProvider client={queryClient}>
              <GrowthBookProvider>
                <CacheProvider value={chakraLayerCache}>
                  <ChakraProvider theme={theme} resetCSS={false}>
                    <OrganizationProvider>
                      <AnalyticsProvider>
                        <JotaiProvider store={appStore}>
                          <HydrateAtoms queryClient={queryClient}>
                            <ChatConnection />
                            <ChatDrawer />
                            <SchedulingDrawer />
                            <Toaster position="bottom-left" richColors />
                            <PrefetchProviderConfig />
                            <PhotonClientRoot />
                            <PersistentLayout>
                              <Component {...pageProps} />
                            </PersistentLayout>
                          </HydrateAtoms>
                        </JotaiProvider>
                      </AnalyticsProvider>
                    </OrganizationProvider>
                  </ChakraProvider>
                </CacheProvider>
              </GrowthBookProvider>
            </QueryClientProvider>
          </ClerkProvider>
        )}
      </AppContext.Provider>
    </ErrorBoundary>
  );
}

export default trpc.withTRPC(App);
